/**
 * Pakete — buchbare Bündel + Preis-Anker (Preis/Gast) für die KI. Unter dem Katalog erreichbar
 * (kein eigener Nav-Eintrag, KISS: Sidebar ≤ 9). Liste + Anlegen + Duplizieren; Bearbeiten ist
 * seit Punkt 129 eine eigene Seite (`pages/paket-bearbeiten.tsx`, `/katalog/pakete/:id`). API: /api/packages (CRUD) + Menues (/api/packages/:id/menus) +
 * PUT /api/packages/:id/items?menuId=.
 *
 * Migration 390 (korrigiertes Verstaendnis, Mockup a01318e7, 07.09.2026): ein "Menü" ist NICHT
 * dasselbe wie ein "Paket" - es gibt KEINE "nur eines aktiv"-Regel ZWISCHEN Paket-Kacheln (der
 * Archivieren/Wiederherstellen-Umschalter je Kachel unten ist ein DAVON UNABHAENGIGES Feld,
 * `archivedAt` - kein Standard-Schalter). Stattdessen kann EIN
 * Paket mehrere benannte Menues (Zusammenstellungen seiner Positionen) tragen; genau eines je
 * Paket ist aktiv und wird verwendet (z. B. vom KI-Auto-Entwurf).
 */
import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from "react";
import { DndContext, KeyboardSensor, PointerSensor, closestCenter, useSensor, useSensors, type DragEndEvent } from "@dnd-kit/core";
import { SortableContext, arrayMove, rectSortingStrategy, sortableKeyboardCoordinates, useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { useNavigate } from "react-router-dom";
import { useApi, apiMutate } from "../hooks/useApi";
import { useContentRefetch } from "../ui/CopilotProvider";
import { paketPreisart, type PaketUebersetzungen } from "@maestro/pricing";
import { money, parseGermanEurosToCents } from "../ui/format";
import { Button, Card, Drawer, EmptyState, ErrorNote, Icon, Input, ListenReiter, LoadingRows, PageHeader, SortierMenue, useBestaetigung, Checkbox } from "../ui/components";
import { WebsiteKategorienKarte, type PaketKategorie } from "../ui/WebsiteKategorienKarte";
import { useListSort, type SortSpalte } from "../hooks/useListSort";
import { useUrlZustand } from "../hooks/useUrlZustand";
import { oeffneGlobaleSuche } from "../lib/globale-suche";
import { AllergenBadges } from "../ui/lmiv";
import { teilReihenfolgeEinsetzen } from "../lib/reihenfolge";

export interface Pkg {
  id: string;
  name: string;
  description: string | null;
  kind: string;
  pricePerPersonCents: number;
  /** Pauschalbetrag bei kind === "flat" (Migration 242) - EIN Betrag fuer die ganze Veranstaltung. */
  priceFlatCents?: number;
  /** Punkt R (Migration 716): Getränkeanteil, Einheit wie der Paketpreis; null = Schätzung 70/30. */
  drinksShareCents?: number | null;
  vatClass: string;
  /** P4 (contract): einzige Wahrheit fuer „archiviert“, NULL = nicht archiviert. Server-seitig
   *  aus `archived` im PATCH berechnet (§4.4) - der Client schreibt den Zeitstempel nie selbst. */
  archivedAt?: string | null;
  itemsVersion?: number;
  itemCount?: number;
  minGuests?: number | null;
  /** Pakete-Widget P1 (Migration 668): auf der Website sichtbar, EN/IT/FR-Texte, Website-Kategorien. */
  websiteVisible?: boolean;
  translations?: PaketUebersetzungen | null;
  categoryIds?: string[];
  /** Migration 750: optionale „Art“ der Website-Karte und Weinbegleitungs-Paket (siehe PaketWebsiteKarte). */
  websiteArt?: string | null;
  winePairingPackageId?: string | null;
  /** Migration 390: Anzahl der Menues dieses Pakets (>= 1, ein Paket ist nie ohne Menue). */
  menuCount?: number;
  /** sql/771: „Gast wählt zwischen diesen Menüs“ - alle Menüs gehen ins Angebot. */
  gastWaehltMenue?: boolean;
  // LMIV-Aggregat über alle Gänge der AKTIVEN Menue (API): Union der Allergen-Codes + ob alle
  // Gänge gepflegt sind.
  allergenUnion?: string[];
  allergensResolved?: boolean;
  dietaryAll?: Record<string, boolean>;
  // P2 (Speisekarten): Paket referenziert archivierte Gänge (z. B. nach „Karte ersetzen").
  hasArchivedItems?: boolean;
  /** Letzte Änderung (Spalte `updated_at`, kommt mit `select()` aus GET /api/packages). */
  updatedAt?: string;
}

/** Listenstandard (Brief § 9, Punkt 95): Kachelraster ohne Spaltenköpfe — Sortierung als Menü.
 *  Standard = Server-Reihenfolge (sortOrder, dann Name). Pauschalpakete haben keinen Preis pro
 *  Person und stehen bei „Preis p. P.“ deshalb immer am Ende. */
const PAKET_SPALTEN: SortSpalte<Pkg>[] = [
  { key: "name", label: "Name", type: "text", accessor: (p) => p.name },
  { key: "preis", label: "Preis p. P.", type: "money", accessor: (p) => (paketPreisart(p).pauschal ? null : paketPreisart(p).betragCents) },
  { key: "geaendert", label: "Zuletzt geändert", type: "date", accessor: (p) => p.updatedAt },
];
const PAKET_SORTIERUNG = [
  { label: "Standard", key: null },
  { label: "Name A–Z", key: "name", dir: "asc" },
  { label: "Preis p. P. aufsteigend", key: "preis", dir: "asc" },
  { label: "Preis p. P. absteigend", key: "preis", dir: "desc" },
  { label: "Zuletzt geändert", key: "geaendert", dir: "desc" },
] as const;

export default function PaketePage() {
  const { bestaetigen, dialog: bestaetigungsDialog } = useBestaetigung();
  const navigate = useNavigate();
  const { data, loading, error, reload } = useApi<Pkg[]>("/api/packages");
  // Review-Befund 31.07.2026: Vier Melde-Stellen im Assistenten, kein Zuhoerer.
  useContentRefetch("package", reload);
  const [createOpen, setCreateOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  // P3 (§5, dasselbe Muster wie catalog.tsx): Standardansicht zeigt nur nicht archivierte
  // Pakete, die Checkbox schaltet auf alle um.
  // Punkt 115 (Paket 3): Suche und „Archivierte zeigen“ stehen in der Adresse (`useUrlZustand`).
  const [zustand, setzeZustand] = useUrlZustand({ q: "", archiv: "", ansicht: "" });
  // Pakete-Widget P1: zweiter Reiter „Website-Kategorien“.
  const kategorienAnsicht = zustand.ansicht === "kategorien";
  const kategorienQ = useApi<PaketKategorie[]>("/api/package-categories");
  const showArchived = zustand.archiv === "1";
  const setShowArchived = (an: boolean) => setzeZustand({ archiv: an ? "1" : "" });
  const suche = zustand.q.trim().toLowerCase();

  // Drag & Drop (12.10.2026): lokale Reihenfolge fuer das optimistische Umsortieren. Kommen
  // frische Daten vom Server, gilt wieder dessen Reihenfolge (sortOrder, dann Name).
  const [ordnung, setOrdnung] = useState<string[] | null>(null);
  useEffect(() => setOrdnung(null), [data]);
  const allPackages = useMemo(() => {
    const liste = data ?? [];
    if (!ordnung) return liste;
    const pos = new Map(ordnung.map((id, i) => [id, i]));
    return [...liste].sort((a, b) => (pos.get(a.id) ?? Infinity) - (pos.get(b.id) ?? Infinity));
  }, [data, ordnung]);
  const sort = useListSort("pakete", PAKET_SPALTEN);
  // Punkt 115 (G1): die Suche (Name, Beschreibung) wirkt vor dem Archiv-Schalter — so lässt sich
  // sagen, wie viele Treffer unter den Archivierten liegen.
  const suchTreffer = useMemo(
    () => (suche ? allPackages.filter((p) => `${p.name} ${p.description ?? ""}`.toLowerCase().includes(suche)) : allPackages),
    [allPackages, suche],
  );
  const archivierteTreffer = suchTreffer.filter((p) => p.archivedAt != null).length;
  const packages = sort.sortiere(
    useMemo(() => suchTreffer.filter((p) => (showArchived ? true : p.archivedAt == null)), [suchTreffer, showArchived]),
  );

  // Umsortieren geht nur in der Standard-Reihenfolge ohne Suche - sonst waere unklar, wohin ein
  // Paket zwischen ausgeblendeten Treffern rutscht.
  const sortierbar = sort.sortKey == null && !suche;
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const namen = new Map(allPackages.map((p) => [p.id, p.name]));
  const name = (id: string | number) => `„${namen.get(String(id)) ?? "Paket"}“`;
  async function onDragEnd({ active, over }: DragEndEvent) {
    if (!over || active.id === over.id) return;
    const sichtbar = packages.map((p) => p.id);
    const neu = arrayMove(sichtbar, sichtbar.indexOf(String(active.id)), sichtbar.indexOf(String(over.id)));
    const ids = teilReihenfolgeEinsetzen(allPackages.map((p) => p.id), neu);
    setOrdnung(ids);
    setFormError(null);
    try {
      await apiMutate("/api/packages/order", "PUT", { ids });
      reload();
    } catch (e) {
      setOrdnung(null);
      setFormError(`Reihenfolge nicht gespeichert: ${e instanceof Error ? e.message : String(e)}`);
    }
  }

  // Punkt 129: Bearbeiten ist eine eigene Seite (/katalog/pakete/:id, pages/paket-bearbeiten.tsx).
  async function saveNew(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = new FormData(e.currentTarget);
    // N-F2: ungültigen Preis als Fehler melden statt still auf 0 zu clampen.
    const priceCents = parseGermanEurosToCents((form.get("price") as string) ?? "");
    if (priceCents == null) {
      setFormError("Ungültiger Preis pro Gast – bitte als Betrag eingeben, z. B. 45,00.");
      return;
    }
    setBusy(true);
    setFormError(null);
    try {
      const created = await apiMutate<{ id: string }>("/api/packages", "POST", {
        name: form.get("name"),
        description: (form.get("description") as string) || undefined,
        pricePerPersonCents: priceCents,
      });
      setCreateOpen(false);
      navigate(`/katalog/pakete/${created.id}`);
    } catch (err) {
      setFormError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  // Punkt 129: Kopie samt Menüs + Positionen, danach direkt in den Editor der Kopie.
  async function duplicatePkg(p: Pkg) {
    setBusy(true);
    try {
      const copy = await apiMutate<{ id: string }>(`/api/packages/${p.id}/duplicate`, "POST", {});
      navigate(`/katalog/pakete/${copy.id}`);
    } catch (e) {
      setFormError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function patchPkg(p: Pkg, patch: Partial<Pkg> & { archived?: boolean }) {
    // M-O: Fehler nicht mehr verschlucken — sonst wirkt ein fehlgeschlagenes PATCH wie „gespeichert".
    try {
      await apiMutate(`/api/packages/${p.id}`, "PATCH", patch);
      reload();
    } catch (e) {
      setFormError(e instanceof Error ? e.message : String(e));
    }
  }

  async function removePkg(p: Pkg) {
    if (!(await bestaetigen({ titel: `Paket „${p.name}“ löschen?`, bestaetigen: "Löschen", gefahr: true }))) return;
    await apiMutate(`/api/packages/${p.id}`, "DELETE").catch((e) => setFormError(e instanceof Error ? e.message : String(e)));
    reload();
  }

  return (
    <>
      {bestaetigungsDialog}
      <PageHeader
        title="Pakete"
        subtitle="Fertige Vorschläge, die Sie in ein Angebot übernehmen – Preis-Anker pro Gast, an denen sich die KI-Angebotserstellung ausrichtet."
        actions={
          kategorienAnsicht ? undefined : (
          /* P2 (§ 4.3): der Kopf traegt nur Aktionen dieser Seite; der Weg zurueck steht in der
             Seitenleiste („Katalog → Karten" / „Pakete", P0). */
          <Button type="button" variant="primary" onClick={() => setCreateOpen(true)}>
            <Icon name="add" className="text-[18px]" /> Paket anlegen
          </Button>
          )
        }
      />

      {formError && <ErrorNote message={formError} />}

      <div className="mb-4">
        <ListenReiter
          label="Pakete oder Website-Kategorien"
          reiter={[
            { key: "pakete", label: "Pakete", anzahl: allPackages.filter((p) => p.archivedAt == null).length },
            { key: "kategorien", label: "Website-Kategorien", anzahl: (kategorienQ.data ?? []).filter((k) => k.archivedAt == null).length },
          ]}
          aktiv={kategorienAnsicht ? "kategorien" : "pakete"}
          onWahl={(k) => setzeZustand({ ansicht: k === "kategorien" ? "kategorien" : "" })}
        />
      </div>

      {kategorienAnsicht ? (
        kategorienQ.error ? (
          <Card className="overflow-hidden">
            <ErrorNote zentriert title="Kategorien konnten nicht geladen werden" message={kategorienQ.error} onRetry={kategorienQ.reload} />
          </Card>
        ) : kategorienQ.loading && !kategorienQ.data ? (
          <LoadingRows />
        ) : (
          <WebsiteKategorienKarte kategorien={kategorienQ.data ?? []} neuLaden={kategorienQ.reload} />
        )
      ) : (
      <>
      {/* P3 (§5, dasselbe Muster wie catalog.tsx): "Archivierte zeigen" schaltet die
          Standardansicht (nur nicht archivierte Pakete) auf alle um. Daneben das Sortiermenü
          (Listenstandard, Punkt 95). */}
      {allPackages.length > 0 && (
        <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-3">
            <Input
              type="search"
              value={zustand.q}
              onChange={(e) => setzeZustand({ q: e.target.value })}
              placeholder="Pakete durchsuchen …"
              aria-label="Pakete durchsuchen"
              className="w-64 max-w-full"
            />
            <label className="flex cursor-pointer items-center gap-2 font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim">
              <Checkbox checked={showArchived} onChange={(e) => setShowArchived(e.target.checked)} />
              Archivierte zeigen
            </label>
          </div>
          <SortierMenue optionen={PAKET_SORTIERUNG} sortKey={sort.sortKey} dir={sort.dir} onWahl={sort.setze} />
        </div>
      )}
      {allPackages.length > 1 && (
        <p className="mb-3 font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
          {sortierbar
            ? "Reihenfolge per Griff ändern (ziehen oder Leertaste + Pfeiltasten). Sie gilt auch im Angebot beim Übernehmen."
            : "Umsortieren geht nur in der Sortierung „Standard“ ohne Suche."}
        </p>
      )}

      {loading ? (
        <LoadingRows />
      ) : error ? (
        <Card className="overflow-hidden">
          <ErrorNote zentriert title="Pakete konnten nicht geladen werden" message={error} onRetry={reload} />
        </Card>
      ) : allPackages.length === 0 ? (
        <Card className="overflow-hidden">
          <EmptyState icon="inventory_2" title="Noch keine Pakete" hint="Lege ein Paket mit Preis pro Gast an – die KI nutzt es als Anker für realistische Angebote." />
        </Card>
      ) : packages.length === 0 ? (
        <Card className="overflow-hidden">
          <EmptyState
            icon={suche ? "search_off" : "inventory_2"}
            title={suche ? `Kein Paket passt zu „${zustand.q.trim()}“` : "Keine Pakete gefunden"}
            hint={suche ? undefined : "Alle Pakete sind archiviert – „Archivierte zeigen” anhaken, um sie zu sehen."}
            andereTreffer={suche && !showArchived ? [{ label: "Archiviert", anzahl: archivierteTreffer, onClick: () => setShowArchived(true) }] : undefined}
            link={[
              ...(!suche ? [{ label: "Alle Pakete anzeigen", onClick: () => setShowArchived(true) }] : []),
              ...(suche ? [{ label: "In allen Bereichen suchen", onClick: () => oeffneGlobaleSuche(zustand.q.trim()) }] : []),
              ...(suche ? [{ label: "Suche aufheben", onClick: () => setzeZustand({ q: "" }) }] : []),
            ]}
          />
        </Card>
      ) : (
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={(e) => void onDragEnd(e)}
          accessibility={{
            screenReaderInstructions: { draggable: "Leertaste zum Aufnehmen, Pfeiltasten zum Verschieben, Leertaste zum Ablegen, Escape zum Abbrechen." },
            announcements: {
              onDragStart: ({ active }) => `${name(active.id)} aufgenommen.`,
              onDragOver: ({ active, over }) => (over ? `${name(active.id)} über ${name(over.id)}.` : undefined),
              onDragEnd: ({ active, over }) => (over ? `${name(active.id)} abgelegt bei ${name(over.id)}.` : `${name(active.id)} abgelegt.`),
              onDragCancel: ({ active }) => `Verschieben von ${name(active.id)} abgebrochen.`,
            },
          }}
        >
        <SortableContext items={packages.map((p) => p.id)} strategy={rectSortingStrategy} disabled={!sortierbar}>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {/* grid-cols-1 + Kachel min-w-0: sonst waechst die Spur mobil auf die volle Breite des
              (truncate-)Titels und die Kachel ragt rechts aus dem Bild. */}
          {packages.map((p) => (
            <SortierbareKachel key={p.id} id={p.id} name={p.name} sortierbar={sortierbar}>
              {(griff) => (
            <Card className="flex h-full min-w-0 flex-col gap-3 p-5">
              <div className="flex items-start justify-between gap-2">
                <div className="flex min-w-0 items-start gap-1">
                {griff}
                <div className="min-w-0">
                  <p className="truncate font-body-lg text-body-lg font-semibold text-on-background dark:text-inverse-on-surface">{p.name}</p>
                  {p.description && <p className="mt-0.5 line-clamp-2 font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">{p.description}</p>}
                </div>
                </div>
                <span className="flex shrink-0 items-center gap-1.5">
                  {p.hasArchivedItems && (
                    <span
                      className="inline-flex items-center gap-1 rounded-full bg-secondary/15 px-2 py-0.5 font-label-md text-[11px] font-semibold text-secondary dark:text-[#b8ae9e]"
                      title="Enthält archivierte Positionen (z. B. nach »Karte ersetzen«) — Paket prüfen und ggf. Gänge austauschen."
                    >
                      <Icon name="warning" className="text-[13px]" /> prüfen
                    </span>
                  )}
                  {/* P3 (§4.4, Vorbild catalog.tsx): einheitliches Wort "archiviert" statt
                      "inaktiv" - `p.archivedAt` ist die einzige Quelle (P4). */}
                  {p.archivedAt != null && (
                    <span className="rounded-full bg-surface-container-high px-2 py-0.5 text-label-md dark:bg-white/[0.08]">archiviert</span>
                  )}
                </span>
              </div>
              {/* Migration 242: Ein Pauschalpaket (kind = „flat") traegt EINEN Betrag fuer die
                  ganze Veranstaltung — „/ Gast" daran waere genau die Verwechslung, aus der im
                  Angebot das Siebzigfache wurde. Welche Spalte gilt, sagt paketPreisart(). */}
              <div className="flex items-baseline gap-2">
                <span className="font-display-lg text-headline-sm font-bold text-primary">{money(paketPreisart(p).betragCents)}</span>
                <span className="font-body-sm text-body-sm text-secondary dark:text-secondary-fixed-dim">
                  {paketPreisart(p).pauschal ? "pauschal (alle Gäste)" : "/ Gast"} · {p.itemCount ?? 0} Positionen
                </span>
              </div>
              {/* Migration 390: mehrere Menues je Paket moeglich - Hinweis nur, wenn es mehr als
                  eines gibt (der Normalfall mit genau einem Menue bleibt unauffaellig). */}
              {(p.menuCount ?? 1) > 1 && (
                <p className="font-body-sm text-[11px] text-secondary dark:text-secondary-fixed-dim">{p.menuCount} Menüs hinterlegt</p>
              )}
              {paketPreisart(p).betragAusPersonenSpalte && (
                <p className="rounded-lg bg-secondary/10 px-2 py-1 font-body-sm text-[12px] text-secondary dark:text-[#b8ae9e]">
                  Der Pauschalbetrag steht noch im Feld „Preis pro Gast". Im Angebot wird er als Pauschale gerechnet – bitte hier als Pauschalpreis speichern.
                </p>
              )}
              {/* LMIV: Allergen-Union über alle Gänge — IMMER sichtbar, solange Positionen existieren.
                  Unvollständig gepflegte Gänge werden ausdrücklich markiert (nie stilles „keine"). */}
              {(p.itemCount ?? 0) > 0 && (
                <div className="flex flex-wrap items-center gap-1.5">
                  <span className="font-label-md text-[11px] uppercase tracking-wider text-secondary dark:text-secondary-fixed-dim">Allergene</span>
                  {p.allergensResolved === false ? (
                    <span
                      className="inline-flex items-center gap-1 rounded bg-secondary/15 px-1.5 py-0.5 font-label-md text-[11px] font-semibold text-secondary dark:text-[#b8ae9e]"
                      title="Mindestens ein Gang ohne gepflegte Allergenangabe – bitte im Katalog prüfen (LMIV)"
                    >
                      <Icon name="warning" className="text-[13px]" /> unvollständig
                    </span>
                  ) : null}
                  <AllergenBadges
                    allergens={p.allergensResolved === false && (p.allergenUnion ?? []).length === 0 ? null : (p.allergenUnion ?? [])}
                    dietary={p.dietaryAll}
                  />
                </div>
              )}
              <div className="mt-auto flex items-center gap-2 border-t border-outline-variant/30 pt-3 dark:border-outline/30">
                <Button type="button" variant="secondary" className="flex-1 justify-center" onClick={() => navigate(`/katalog/pakete/${p.id}`)}>
                  <Icon name="edit" className="text-[16px]" /> Bearbeiten
                </Button>
                <button type="button" className="rounded-lg p-2 text-secondary hover:bg-surface-container-high disabled:opacity-50 dark:hover:bg-surface-variant/30" title="Duplizieren" aria-label="Duplizieren" disabled={busy} onClick={() => void duplicatePkg(p)}>
                  <Icon name="content_copy" className="text-[18px]" />
                </button>
                {/* P3 (§4.4): "Archivieren"/"Wiederherstellen" statt "Deaktivieren"/"Aktivieren"
                    - dasselbe Wort wie bei Karten/Kategorien/Positionen (Vorbild catalog.tsx). */}
                <button type="button" className="rounded-lg p-2 text-secondary hover:bg-surface-container-high dark:hover:bg-surface-variant/30" title={p.archivedAt == null ? "Archivieren" : "Wiederherstellen"} onClick={() => void patchPkg(p, { archived: p.archivedAt == null })}>
                  <Icon name={p.archivedAt == null ? "toggle_on" : "toggle_off"} />
                </button>
                <button type="button" className="rounded-lg p-2 text-error hover:bg-error/10" title="Löschen" onClick={() => void removePkg(p)}>
                  <Icon name="delete" className="text-[18px]" />
                </button>
              </div>
            </Card>
              )}
            </SortierbareKachel>
          ))}
        </div>
        </SortableContext>
        </DndContext>
      )}
      </>
      )}

      {/* Anlegen */}
      <Drawer open={createOpen} onClose={() => setCreateOpen(false)} title="Paket anlegen">
        <form className="space-y-4" onSubmit={(e) => void saveNew(e)}>
          {formError && <ErrorNote message={formError} />}
          <label className="block">
            <span className="mb-1 block font-body-sm text-body-sm font-semibold">Name *</span>
            <Input name="name" required placeholder="z. B. Menü Klassik" />
          </label>
          <label className="block">
            <span className="mb-1 block font-body-sm text-body-sm font-semibold">Beschreibung</span>
            <Input name="description" placeholder="kurz, optional" />
          </label>
          <label className="block">
            <span className="mb-1 block font-body-sm text-body-sm font-semibold">Preis pro Gast (€) *</span>
            <Input name="price" required inputMode="decimal" placeholder="45,00" />
          </label>
          <div className="flex justify-end gap-3 pt-2">
            <Button type="button" variant="secondary" onClick={() => setCreateOpen(false)}>Abbrechen</Button>
            <Button type="submit" variant="primary" disabled={busy}>{busy ? "Speichert …" : "Anlegen"}</Button>
          </div>
        </form>
      </Drawer>
    </>
  );
}

/** Eine Paket-Kachel als dnd-kit-Sortierelement; gezogen wird nur am Griff (Knopf, per Tastatur
 *  erreichbar), damit Klicks auf „Bearbeiten“ & Co. nicht zum Ziehen werden. */
function SortierbareKachel({ id, name, sortierbar, children }: { id: string; name: string; sortierbar: boolean; children: (griff: ReactNode) => ReactNode }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id, disabled: !sortierbar });
  const griff = sortierbar ? (
    <button
      type="button"
      className="-ml-2 cursor-grab touch-none rounded p-0.5 text-secondary hover:bg-surface-container-high dark:hover:bg-surface-variant/30"
      aria-label={`${name} verschieben`}
      title="Ziehen zum Umsortieren"
      {...attributes}
      {...listeners}
    >
      <Icon name="drag_indicator" className="text-[18px]" />
    </button>
  ) : null;
  return (
    <div
      ref={setNodeRef}
      className="min-w-0"
      style={{ transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.6 : 1, position: "relative", zIndex: isDragging ? 10 : undefined }}
    >
      {children(griff)}
    </div>
  );
}
